import {
  Abc,
  Chat,
  Check,
  DatasetOutlined,
  GridView,
  InsertDriveFile,
  Style,
  TableChart,
  ViewList,
} from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEntityActions, useEntityChildren } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCurrentModal } from 'components/modal';
import { useNotifications } from 'components/notify';
import React, { useEffect, useMemo, useState } from 'react';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';

import { ViewType } from '../finsightEntity.view';

type ConfigureDefaultsModalProps = {
  entity: FinsightEntity;
};

const VIEW_TYPE_OPTIONS: Array<{
  value: ViewType;
  label: string;
  icon: React.ReactNode;
}> = [
  { value: 'list', label: 'List View', icon: <ViewList /> },
  { value: 'card', label: 'Card View', icon: <GridView /> },
  { value: 'table', label: 'Table View', icon: <DatasetOutlined /> },
  { value: 'text', label: 'Text View', icon: <Abc /> },
  { value: 'spreadsheet', label: 'Spreadsheet View', icon: <TableChart /> },
  { value: 'discussion', label: 'Discussion View', icon: <Chat /> },
  { value: 'files', label: 'Files View', icon: <InsertDriveFile /> },
  { value: 'flashcards', label: 'Flashcards View', icon: <Style /> },
];

export default function ConfigureDefaultsModal(
  props: ConfigureDefaultsModalProps,
) {
  const { entity } = props;
  const modalCtx = useCurrentModal();
  const { showNotification } = useNotifications();
  const { createEntity, updateEntity } = useEntityActions();
  const workspaceId = useWorkspaceId();
  const { mutate } = useSWRConfig();

  const { data: children, isLoading: isLoadingChildren } = useEntityChildren(
    entity.id,
    false,
  );

  const configurationEntity = useMemo(() => {
    return children?.find(
      (child) => child.types[0] === 'blackframe.internal.ui.configuration',
    );
  }, [children]);

  const [selectedViewMode, setSelectedViewMode] = useState<ViewType | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (configurationEntity?.extraFields?.viewMode) {
      setSelectedViewMode(configurationEntity.extraFields.viewMode as ViewType);
    } else {
      setSelectedViewMode(null);
    }
  }, [configurationEntity]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (configurationEntity) {
        // Update existing configuration entity
        await updateEntity(configurationEntity.id, {
          extraFields: {
            ...configurationEntity.extraFields,
            viewMode: selectedViewMode,
          },
        });
      } else {
        // Create new configuration entity
        const response = await createEntity({
          name: 'UI Configuration',
          type: 'blackframe.internal.ui.configuration',
          parent: entity.id,
          extraFields: {
            viewMode: selectedViewMode,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to create configuration');
        }
      }

      // Invalidate cache for children
      mutate(
        (key) =>
          typeof key === 'string' &&
          key.includes(
            `/workspace/${workspaceId}/entity/${entity.id}/children`,
          ),
      );

      showNotification('Default view mode saved', { variant: 'success' });
      modalCtx?.closeModal();
    } catch (error) {
      showNotification('Failed to save default view mode', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap={2} sx={{ minWidth: 400 }}>
      <Typography variant="h6">Configure Default View Mode</Typography>
      <Typography variant="body2" color="text.secondary">
        Set the default view mode for this entity&apos;s children
      </Typography>

      {isLoadingChildren ? (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Stack gap={1}>
          {VIEW_TYPE_OPTIONS.map((option) => (
            <ListItemButton
              key={option.value}
              selected={selectedViewMode === option.value}
              onClick={() => setSelectedViewMode(option.value)}
            >
              <ListItemIcon>
                {selectedViewMode === option.value ? <Check /> : option.icon}
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </ListItemButton>
          ))}
        </Stack>
      )}

      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button onClick={() => modalCtx?.closeModal()} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || selectedViewMode === null}
        >
          {loading ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
}
